import { useEffect, useState, ChangeEvent } from "react";
import { Params, useParams, useNavigate } from "react-router-dom";
import { Button } from "@mui/material";

import "./style.scss";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import {
    fetchRecognitionResult,
    saveRecognitionResult
} from "../../../api/admin/recognition-result/endpoints-referrers";
import { fetchLearningObject } from "../../../api/admin/content-providers/endpoints-referrers";
import { RecognitionResult } from "../../../common/interfaces/admin/recognition-result.interface";
import { LoaderView } from "../../../components/loader/loader.comp";
import { RRSelectionFilter } from "../../../components/admin/rr-selection-filter/rr-selection-filter.comp";
import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import FeedbackView from "../feedback/feedback";

const INLINE_TAGS = new Set([
    "a",
    "abbr",
    "b",
    "br",
    "cite",
    "code",
    "em",
    "i",
    "img",
    "input",
    "label",
    "small",
    "span",
    "strong",
    "sub",
    "sup",
    "time",
    "u"
]);

const SELF_CLOSING_TAGS = new Set(["br", "hr", "img", "input", "meta", "link"]);

const formatHtml = (html: string): string => {
    // Strip HTML comments (page markers) and handle them separately
    const tokens: string[] = [];
    const tagRegex = /(<\/?[a-zA-Z][^>]*\/?>|<!--[\s\S]*?-->)/g;
    let lastIndex = 0;
    let match;

    while ((match = tagRegex.exec(html)) !== null) {
        const textBefore = html.slice(lastIndex, match.index).trim();
        if (textBefore) tokens.push(textBefore);
        tokens.push(match[1]);
        lastIndex = match.index + match[0].length;
    }
    const trailing = html.slice(lastIndex).trim();
    if (trailing) tokens.push(trailing);

    const lines: string[] = [];
    let indent = 0;
    const INDENT = "  ";

    for (const token of tokens) {
        // HTML comment (page markers)
        if (token.startsWith("<!--")) {
            lines.push("");
            lines.push(INDENT.repeat(indent) + token);
            continue;
        }

        // Closing tag
        if (token.startsWith("</")) {
            indent = Math.max(0, indent - 1);
            lines.push(INDENT.repeat(indent) + token);
            continue;
        }

        // Opening tag
        if (token.startsWith("<")) {
            const tagName = (token.match(/^<([a-zA-Z][a-zA-Z0-9]*)/)?.[1] ?? "").toLowerCase();

            if (INLINE_TAGS.has(tagName)) {
                // Inline tags: append to previous line if possible
                if (lines.length > 0) {
                    lines[lines.length - 1] += token;
                } else {
                    lines.push(INDENT.repeat(indent) + token);
                }
                continue;
            }

            lines.push(INDENT.repeat(indent) + token);

            if (!SELF_CLOSING_TAGS.has(tagName) && !token.endsWith("/>")) {
                indent++;
            }
            continue;
        }

        // Text content
        if (lines.length > 0 && !lines[lines.length - 1].endsWith(">")) {
            // Append to previous line if it's an inline context
            lines[lines.length - 1] += token;
        } else {
            lines.push(INDENT.repeat(indent) + token);
        }
    }

    return lines.join("\n");
};

const RecognitionResultView = () => {
    const navigate = useNavigate();
    const { providerId, uid } = useParams<Params<string>>();
    const { user, isTrial, isSuperAdmin } = FirebaseAuthContainer.useContainer();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [recognitionResult, setRecognitionResult] = useState<RecognitionResult>();
    const [rrText, setRrText] = useState<string>();
    const [rrList, setRrList] = useState<RecognitionResult[]>([]);
    const [feedbackOpen, setFeedbackOpen] = useState<boolean>(false);

    useEffect(() => {
        setIsLoading(true);

        if (!user || !uid) return;

        fetchRecognitionResult(user.companyId, uid).then(response => {
            setRecognitionResult(response.data);
        });
    }, [user, uid]);

    useEffect(() => {
        if (isTrial) {
            setIsLoading(false);
            return;
        }
        if (!user || !recognitionResult) return;

        fetchLearningObject(user.companyId, recognitionResult.courseId, recognitionResult.loItemId)
            .then(response => {
                setRrList(response.data.recognitionResults);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [isTrial, user, recognitionResult]);

    useEffect(() => {
        const rawText = recognitionResult?.recognitionResultData?.loItemText ?? "";
        try {
            const parsed = JSON.parse(rawText);
            if (parsed && typeof parsed === "object" && parsed.format_version) {
                const content = parsed.content ?? "";
                const metadataObj = { ...parsed };
                delete metadataObj.content;
                const prettyMetadata = JSON.stringify(metadataObj, null, 2);
                const formattedHtml = formatHtml(content);
                setRrText(prettyMetadata + "\n\n" + formattedHtml);
            } else {
                setRrText(rawText);
            }
        } catch {
            setRrText(rawText);
        }
    }, [recognitionResult]);

    const handleBackClick = () => {
        if (recognitionResult) {
            const link = !isTrial
                ? `/knowledge-base/${providerId}/${recognitionResult.courseId}`
                : `/knowledge-base`;
            navigate(link);
        }
    };

    const handleChangeSelectedRR = (rr: RecognitionResult) => {
        if (!providerId) return;
        navigate(`/recognition-result/${providerId}/${rr.uid}`);
    };

    const handleCompareClick = () => {
        if (!recognitionResult || !providerId) return;
        navigate(`/compare-recognition-result/${providerId}/${recognitionResult.uid}`);
    };

    const handleSaveClick = () => {
        if (!user || !uid) return;

        setIsLoading(true);
        saveRecognitionResult(user.companyId, uid, {
            loItemText: rrText ?? "",
            summary: recognitionResult?.recognitionResultData?.summary ?? "",
            isPublish: true
        })
            .then(response => {
                const {
                    data: { uid }
                } = response;
                navigate(`/recognition-result/${providerId}/${uid}`);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setRrText(event.target.value);
    };

    const handleFeedbackOpen = () => {
        setFeedbackOpen(true);
    };

    const handleFeedbackClose = () => {
        setFeedbackOpen(false);
    };

    return (
        <HeaderLayout
            subheaderHeight={46}
            subheader={
                <div className="rr-header">
                    <div className="rr-header-title">
                        <span className="rr-header-title__text" role="heading" aria-level={1}>
                            Review AI Result
                        </span>
                    </div>
                    {!isTrial && (
                        <div className="rr-header-filter">
                            <RRSelectionFilter
                                list={rrList}
                                selectedItem={recognitionResult}
                                setSelectedItem={handleChangeSelectedRR}
                                rightPosition={true}
                                maxWidth={600}
                            />
                        </div>
                    )}
                </div>
            }
            footerHeight={60}
            footer={
                <div
                    style={{ height: 60 }}
                    className={`rr-footer${isTrial ? " rr-footer--trial" : ""}`}
                >
                    <div className="rr-footer-left">
                        <Button
                            className="secondary-button"
                            variant="outlined"
                            onClick={handleBackClick}
                        >
                            Close
                        </Button>
                        {!isTrial && isSuperAdmin && (
                            <Button
                                className="primary-button rr-footer__button--margin-left"
                                variant="outlined"
                                disabled={
                                    recognitionResult?.recognitionResultData?.loItemText === rrText
                                }
                                onClick={handleSaveClick}
                            >
                                Save
                            </Button>
                        )}
                    </div>
                    <div className="rr-footer-right">
                        {isTrial ? (
                            <>
                                <span className="rr-footer__message">
                                    You can&apos;t edit results in trial version.
                                </span>
                                <button
                                    type="button"
                                    className="rr-footer__link"
                                    onClick={handleFeedbackOpen}
                                >
                                    Upgrade to full version
                                </button>
                            </>
                        ) : isSuperAdmin ? (
                            <Button
                                className="primary-button"
                                variant="outlined"
                                disabled={rrList.length < 2}
                                onClick={handleCompareClick}
                            >
                                Compare
                            </Button>
                        ) : null}
                    </div>
                </div>
            }
        >
            <div className="rr-view">
                {/* {recognitionResult && (
                    <div className="rr-title">
                        <span className="rr-title__text">
                            ver.{recognitionResult.version}&nbsp;
                            {recognitionResult.publisherFullName ?? "Auto"}&nbsp;
                            {formatStringDate(
                                recognitionResult.createdAt,
                                "MM.dd.yyyy — hh:mm"
                            )}
                        </span>
                    </div>
                )} */}
                <div className="rr-content">
                    <textarea
                        className="rr-content__textarea"
                        aria-label="Review AI Result"
                        onChange={isSuperAdmin ? handleTextChange : undefined}
                        readOnly={!isSuperAdmin}
                        value={rrText}
                    />
                </div>
                <LoaderView show={isLoading} />
                <ModalTemplate
                    open={feedbackOpen}
                    width={654}
                    handleClose={handleFeedbackClose}
                    ariaLabel="Upgrade to full version"
                >
                    <FeedbackView handleClose={handleFeedbackClose} />
                </ModalTemplate>
            </div>
        </HeaderLayout>
    );
};

export default RecognitionResultView;
